import { useMemo, useState } from "react";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useAdministrators } from "@/store/administrators/AdministratorsProvider";
import {
  ADMINISTRATEUR_ROLE_OPTIONS,
  ADMINISTRATEUR_STATUT_LABELS,
  ADMINISTRATEUR_STATUT_OPTIONS,
  ADMINISTRATEUR_STATUT_TONES,
  administrateurInitiales,
  administrateurNomComplet,
  administrateurRoleLabel,
  type PlatformAdministrateur,
} from "@/store/administrators/types";
import { formatDate } from "@/utils/format";
import type { ExportColumn } from "@/utils/export";
import ConfirmationModal from "@/pages/super-admin/components/ConfirmationModal";
import AdministrateurForm from "@/pages/super-admin/components/AdministrateurForm";

const roleFilters = [{ value: "", label: "Tous les rôles" }, ...ADMINISTRATEUR_ROLE_OPTIONS];
const statutFilters = [{ value: "", label: "Tous les statuts" }, ...ADMINISTRATEUR_STATUT_OPTIONS];

export default function SuperAdminAdministrateursPage() {
  const { administrateurs, changeStatut, countSuperAdminsActifs, chargement, erreur, recharger } = useAdministrators();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformAdministrateur | null>(null);
  const [pendingToggle, setPendingToggle] = useState<PlatformAdministrateur | null>(null);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("");

  const isProtected = (admin: PlatformAdministrateur) =>
    admin.role === "super_admin" && admin.statut === "actif" && countSuperAdminsActifs() <= 1;

  const stats = useMemo(
    () => ({
      total: administrateurs.length,
      actifs: administrateurs.filter((item) => item.statut === "actif").length,
      desactives: administrateurs.filter((item) => item.statut === "desactive").length,
      superAdmins: administrateurs.filter((item) => item.role === "super_admin").length,
    }),
    [administrateurs]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return administrateurs
      .filter((item) => {
        if (roleFilter && item.role !== roleFilter) return false;
        if (statutFilter && item.statut !== statutFilter) return false;
        if (term && !`${administrateurNomComplet(item)} ${item.email}`.toLowerCase().includes(term)) return false;
        return true;
      })
      .sort((a, b) => administrateurNomComplet(a).localeCompare(administrateurNomComplet(b), "fr"));
  }, [administrateurs, search, roleFilter, statutFilter]);

  const isFiltering = Boolean(search.trim() || roleFilter || statutFilter);

  const exportColumns: ExportColumn<PlatformAdministrateur>[] = [
    { header: "Prénom", value: (item) => item.prenom },
    { header: "Nom", value: (item) => item.nom },
    { header: "Email", value: (item) => item.email },
    { header: "Téléphone", value: (item) => item.telephone },
    { header: "Rôle", value: (item) => administrateurRoleLabel(item.role) },
    { header: "Statut", value: (item) => ADMINISTRATEUR_STATUT_LABELS[item.statut] },
    { header: "Créé le", value: (item) => formatDate(item.createdAt) },
  ];

  const resetFilters = () => {
    setSearch("");
    setRoleFilter("");
    setStatutFilter("");
  };

  const columns: DataTableColumn<PlatformAdministrateur>[] = [
    {
      key: "identite",
      header: "Administrateur",
      className: "min-w-[230px]",
      render: (administrateur) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 font-heading text-xs font-semibold text-primary-700">
            {administrateurInitiales(administrateur)}
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-label font-medium text-foreground-950">
              {administrateurNomComplet(administrateur)}
            </span>
            <span className="truncate text-xs text-foreground-500">
              {administrateurRoleLabel(administrateur.role)}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "email",
      header: "Email",
      className: "min-w-[220px]",
      render: (administrateur) => <span className="text-foreground-700">{administrateur.email || "—"}</span>,
    },
    {
      key: "telephone",
      header: "Téléphone",
      className: "min-w-[150px]",
      render: (administrateur) => (
        <span className="text-foreground-700">{administrateur.telephone || "Non renseigné"}</span>
      ),
    },
    {
      key: "statut",
      header: "Statut",
      render: (administrateur) => (
        <Badge tone={ADMINISTRATEUR_STATUT_TONES[administrateur.statut]}>
          {ADMINISTRATEUR_STATUT_LABELS[administrateur.statut]}
        </Badge>
      ),
    },
    {
      key: "createdAt",
      header: "Créé le",
      className: "min-w-[130px]",
      render: (administrateur) => <span className="text-foreground-700">{formatDate(administrateur.createdAt)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[230px]",
      render: (administrateur) => {
        const actif = administrateur.statut === "actif";
        const protectedRow = isProtected(administrateur);
        return (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setPendingToggle(administrateur)}
              disabled={protectedRow && actif}
              title={protectedRow && actif ? "Le dernier super administrateur actif ne peut pas être désactivé." : undefined}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            >
              <i className={actif ? "ri-forbid-2-line text-base" : "ri-checkbox-circle-line text-base"} aria-hidden="true" />
              {actif ? "Désactiver" : "Activer"}
            </button>
            <CardActions
              onEdit={() => {
                setEditing(administrateur);
                setFormOpen(true);
              }}
            />
          </div>
        );
      },
    },
  ];

  const confirmToggle = () => {
    if (!pendingToggle) return;
    changeStatut(pendingToggle.id, pendingToggle.statut === "actif" ? "desactive" : "actif");
    setPendingToggle(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Administrateurs</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Comptes administratifs internes de la plateforme. Protégez l'accès : le dernier super administrateur actif
            ne peut être ni désactivé ni supprimé.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu
            rows={filtered}
            columns={exportColumns}
            filename="super-admin-administrateurs"
            title="Administrateurs"
            subtitle="Données actuellement affichées"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-admin-line" label="Administrateurs" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Actifs" value={String(stats.actifs)} tone="secondary" />
        <StatCard icon="ri-forbid-2-line" label="Désactivés" value={String(stats.desactives)} tone="accent" />
        <StatCard icon="ri-shield-star-line" label="Super administrateurs" value={String(stats.superAdmins)} tone="neutral" />
      </div>

      {administrateurs.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="adm-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="adm-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nom, email..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="adm-f-role" className="text-xs font-label font-medium text-foreground-700">
                Rôle
              </label>
              <Select id="adm-f-role" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} options={roleFilters} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="adm-f-statut" className="text-xs font-label font-medium text-foreground-700">
                Statut
              </label>
              <Select
                id="adm-f-statut"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                options={statutFilters}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} administrateur{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {administrateurs.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {chargement && administrateurs.length === 0 && (
        <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-14">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
        </div>
      )}

      {!chargement && erreur && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/60 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-label font-medium text-primary-800">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
            {erreur}
          </p>
          <button
            type="button"
            onClick={() => void recharger()}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      {!chargement && !erreur && administrateurs.length === 0 && (
        <EmptyState
          icon="ri-admin-line"
          title="Aucun administrateur"
          description="Aucun super administrateur n'est enregistré. Les comptes super administrateur sont gérés hors interface : ils ne peuvent être ni créés ni supprimés depuis cet écran."
        />
      )}

      {administrateurs.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun administrateur ne correspond à votre recherche ou aux filtres sélectionnés."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(administrateur) => administrateur.id}
          minWidthClass="min-w-[1100px]"
          caption="Administrateurs"
        />
      )}

      <AdministrateurForm open={formOpen} onClose={() => setFormOpen(false)} administrateur={editing} />

      <ConfirmationModal
        open={Boolean(pendingToggle)}
        onClose={() => setPendingToggle(null)}
        onConfirm={confirmToggle}
        title={pendingToggle?.statut === "actif" ? "Désactiver l'administrateur" : "Activer l'administrateur"}
        description={
          pendingToggle
            ? pendingToggle.statut === "actif"
              ? `Désactiver « ${administrateurNomComplet(pendingToggle)} » ? Il ne pourra plus administrer la plateforme.`
              : `Réactiver « ${administrateurNomComplet(pendingToggle)} » ?`
            : ""
        }
        confirmLabel={pendingToggle?.statut === "actif" ? "Désactiver" : "Activer"}
        icon={pendingToggle?.statut === "actif" ? "ri-forbid-2-line" : "ri-checkbox-circle-line"}
        tone={pendingToggle?.statut === "actif" ? "danger" : "primary"}
      />
    </div>
  );
}